import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, CheckCircle2, Bell } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoiceDetail() {
  const { iid } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);

  const load = async () => setInv((await api.get(`/invoices/${iid}`)).data);
  useEffect(() => { load(); }, [iid]);

  if (!inv) return <div className="text-slate-500">Loading…</div>;

  const markPaid = async () => {
    await api.patch(`/invoices/${iid}/status`, { status: "paid" });
    toast.success("Marked paid"); load();
  };
  const remind = async () => {
    await api.post("/reminders/send", { entity_type: "invoice", entity_id: iid, channel: "email" });
    toast.success("Reminder sent");
  };

  return (
    <div data-testid="invoice-detail">
      <button onClick={() => nav("/dashboard/invoices")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to invoices</button>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Invoice {inv.number}</h1>
          <p className="text-sm text-slate-500 mt-1">{inv.customer_name} · <span className={`status-pill status-${inv.status}`}>{inv.status}</span></p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(`${API}/invoices/${iid}/pdf`, "_blank")}><Download className="w-4 h-4 mr-1.5" /> PDF</Button>
          {inv.status !== "paid" && <>
            <Button variant="outline" onClick={remind}><Bell className="w-4 h-4 mr-1.5" /> Reminder</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={markPaid}><CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Paid</Button>
          </>}
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5">
        <div className="font-display font-bold text-lg mb-2">{inv.title}</div>
        <table className="w-full text-sm mt-4">
          <thead className="text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr><th className="text-left py-2">Description</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Price</th><th className="text-right py-2">Amount</th></tr>
          </thead>
          <tbody>
            {inv.line_items.map((li, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2">{li.description}</td>
                <td className="py-2 text-right">{li.quantity} {li.unit}</td>
                <td className="py-2 text-right">{fmt(li.unit_price)}</td>
                <td className="py-2 text-right font-medium">{fmt(li.quantity * li.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 ml-auto max-w-xs text-sm space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmt(inv.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Tax ({inv.tax_rate}%)</span><span>{fmt(inv.tax)}</span></div>
          <div className="flex justify-between font-display font-extrabold text-lg text-blue-600 border-t border-slate-200 pt-2"><span>Total</span><span>{fmt(inv.total)}</span></div>
        </div>
      </div>
    </div>
  );
}
